import { Permission } from '../permission.types'
import { LEGACY_DOCTOR_ROLE_PROFILE, LEGACY_ROLE, LegacyRoleCode } from './legacy-role.ids'

/**
 * Single source of truth: legacy role code → capabilities.
 *
 * To add a new action:
 * 1. Add a value to `Permission` enum.
 * 2. Assign it to the relevant roles below.
 * 3. Use `permissionGuard`, `*hasPermission` or `PermissionService.can()`.
 */
export const LEGACY_ROLE_PERMISSIONS: Record<LegacyRoleCode, readonly Permission[]> = {
  [LEGACY_ROLE.DOCTOR_ADMIN]: [
    Permission.HomeView,
    Permission.ProfileView,
    Permission.ProfileEdit,
    Permission.ProfileERecetaConfig,
    Permission.AbsencesOwn,
    Permission.AbsencesTeam,
    Permission.AbsencesManageTeam,
    Permission.AbsencesDownload,
    Permission.ConsultationsCreate,
    Permission.ConsultationsView,
    Permission.ConsultationsHistory,
    Permission.ConsultationsCancel,
    Permission.ConsultationsResendLink,
    Permission.PatientsList,
    Permission.PatientsDownload,
    Permission.PatientsClinicalHistory,
    Permission.PatientsClinicalHistoryEdit,
    Permission.PatientsClinicalHistoryDeleteDocument,
    Permission.PatientsImport,
    Permission.TeamManageDoctors,
    Permission.TeamManageOperators,
    Permission.TeamAgenda,
    Permission.Faq,
    Permission.ConsultationAttend,
    Permission.ConsultationReassign,
    Permission.ConsultationFinish,
    Permission.ConsultationReport,
    Permission.ConsultationPrescription,
    Permission.ConsultationDerive,
  ],

  [LEGACY_ROLE.DOCTOR_DIRECTOR]: [
    Permission.HomeView,
    Permission.ProfileView,
    Permission.ProfileEdit,
    Permission.ProfileAssignServices,
    Permission.ProfileERecetaConfig,
    Permission.ProfileStripeConnect,
    Permission.AbsencesOwn,
    Permission.AbsencesTeam,
    Permission.AbsencesManageTeam,
    Permission.AbsencesDownload,
    Permission.AgendaOwn,
    Permission.ConsultationsCreate,
    Permission.ConsultationsView,
    Permission.ConsultationsHistory,
    Permission.ConsultationsCancel,
    Permission.ConsultationsResendLink,
    Permission.PatientsList,
    Permission.PatientsDownload,
    Permission.PatientsClinicalHistory,
    Permission.PatientsClinicalHistoryEdit,
    Permission.PatientsClinicalHistoryDeleteDocument,
    Permission.PatientsImport,
    Permission.TeamManageDoctors,
    Permission.TeamManageOperators,
    Permission.TeamAgenda,
    Permission.Faq,
    Permission.ConsultationAttend,
    Permission.ConsultationReassign,
    Permission.ConsultationFinish,
    Permission.ConsultationReport,
    Permission.ConsultationPrescription,
    Permission.ConsultationDerive,
  ],

  [LEGACY_ROLE.DOCTOR_SERVICE_DIRECTOR]: [
    Permission.HomeView,
    Permission.ProfileView,
    Permission.ProfileEdit,
    Permission.ProfileAssignServices,
    Permission.ProfileERecetaConfig,
    Permission.AbsencesOwn,
    Permission.AbsencesTeam,
    Permission.AbsencesDownload,
    Permission.AgendaOwn,
    Permission.ConsultationsCreate,
    Permission.ConsultationsView,
    Permission.ConsultationsHistory,
    Permission.ConsultationsCancel,
    Permission.ConsultationsResendLink,
    Permission.PatientsList,
    Permission.PatientsDownload,
    Permission.PatientsClinicalHistory,
    Permission.PatientsClinicalHistoryEdit,
    Permission.PatientsClinicalHistoryDeleteDocument,
    Permission.PatientsImport,
    Permission.TeamManageDoctors,
    Permission.TeamManageOperators,
    Permission.TeamAgenda,
    Permission.Faq,
    Permission.ConsultationAttend,
    Permission.ConsultationReassign,
    Permission.ConsultationFinish,
    Permission.ConsultationReport,
    Permission.ConsultationPrescription,
    Permission.ConsultationDerive,
  ],

  [LEGACY_DOCTOR_ROLE_PROFILE.SERVICE_DIRECTOR_NO_SCHEDULE]: [
    Permission.ProfileView,
    Permission.ProfileEdit,
    Permission.ProfileAssignServices,
    Permission.ProfileERecetaConfig,
    Permission.AbsencesOwn,
    Permission.AbsencesTeam,
    Permission.AbsencesDownload,
    Permission.ConsultationsCreate,
    Permission.ConsultationsView,
    Permission.ConsultationsHistory,
    Permission.ConsultationsCancel,
    Permission.ConsultationsResendLink,
    Permission.PatientsList,
    Permission.PatientsDownload,
    Permission.PatientsClinicalHistory,
    Permission.PatientsClinicalHistoryEdit,
    Permission.PatientsClinicalHistoryDeleteDocument,
    Permission.PatientsImport,
    Permission.TeamManageDoctors,
    Permission.TeamManageOperators,
    Permission.TeamAgenda,
    Permission.Faq,
    Permission.ConsultationAttend,
    Permission.ConsultationReassign,
    Permission.ConsultationFinish,
    Permission.ConsultationReport,
    Permission.ConsultationPrescription,
    Permission.ConsultationDerive,
  ],

  [LEGACY_ROLE.DOCTOR_PROFESSIONAL]: [
    Permission.HomeView,
    Permission.ProfileView,
    Permission.ProfileERecetaConfig,
    Permission.ProfileStripeConnect,
    Permission.AbsencesOwn,
    Permission.AgendaOwn,
    Permission.ConsultationsCreate,
    Permission.ConsultationsView,
    Permission.ConsultationsHistory,
    Permission.ConsultationsCancelAgenda,
    Permission.ConsultationsResendLink,
    Permission.PatientsList,
    Permission.PatientsClinicalHistory,
    Permission.PatientsClinicalHistoryEdit,
    Permission.PatientsImport,
    Permission.Faq,
    Permission.ConsultationAttend,
    Permission.ConsultationReassign,
    Permission.ConsultationFinish,
    Permission.ConsultationReport,
    Permission.ConsultationPrescription,
    Permission.ConsultationDerive,
  ],

  [LEGACY_ROLE.OPERATOR_ADMIN]: [
    Permission.ProfileView,
    Permission.ConsultationsCreate,
    Permission.ConsultationsHistory,
    Permission.ConsultationsView,
    Permission.ConsultationsCancel,
    Permission.ConsultationsResendLink,
    Permission.PatientsList,
    Permission.PatientsClinicalHistory,
    Permission.PatientsClinicalHistoryEdit,
    Permission.PatientsClinicalHistoryDeleteDocument,
    Permission.PatientsImport,
    Permission.TeamManageOperators,
    Permission.Faq,
  ],

  [LEGACY_ROLE.OPERATOR_ICAS]: [
    Permission.ProfileView,
    Permission.ConsultationsCreate,
    Permission.ConsultationsHistory,
    Permission.ConsultationsView,
    Permission.ConsultationsCancel,
    Permission.ConsultationsResendLink,
    Permission.PatientsList,
    Permission.PatientsClinicalHistory,
    Permission.PatientsClinicalHistoryEdit,
    Permission.PatientsImport,
    Permission.TeamAgenda,
    Permission.ConsultationReassign,
  ],

  [LEGACY_ROLE.OPERATOR_RACC]: [
    Permission.ProfileView,
    Permission.ConsultationsCreate,
    Permission.ConsultationsHistory,
    Permission.ConsultationsResendLink,
    Permission.PatientsList,
  ],

  [LEGACY_ROLE.OPERATOR_RACC_ALIAS]: [
    Permission.ConsultationsCreate,
    Permission.ConsultationsHistory,
    Permission.ConsultationsCancel,
    Permission.ConsultationsResendLink,
  ],

  [LEGACY_ROLE.DOCTOR_SUMMUM_CONTRACT_PAYMENTS]: [Permission.ProfileView, Permission.DoctorContractPayments],
}

export function permissionsForLegacyRoles(roleCodes: readonly LegacyRoleCode[]): Set<Permission> {
  const result = new Set<Permission>()

  for (const code of roleCodes) {
    for (const permission of LEGACY_ROLE_PERMISSIONS[code] ?? []) {
      result.add(permission)
    }
  }

  return result
}
